import React, { useState, useRef, useContext } from 'react';

import { FormattedMessage } from 'react-intl';

import OmView from '@xingternal/xing-view/OmView'; // eslint-disable-line
import { usePopOver } from '@xingternal/pop-over';
import { Menu } from '@xingternal/menu';
import { BodyCopy } from '@xingternal/typography';
import {
  trackEditOpening,
  trackEditSaving,
  trackDelete,
} from '@xingternal/pages-tracking';

import SelectList, { SelectListOption } from '@xingternal/malt-select-list';
import { IconImage, IconUpload, IconTrash } from '@xingternal/icons';
import { XingFrameContext } from '@xingternal/xing-frame-provider';
import { ErrorContext, ErrorType } from '@xingternal/pages-page-error';
import {
  EditButton,
  ExternalEditInfoBanner,
  ImageUpload,
  DialogContextProvider,
  useDialogContext,
  NotSavedDialog,
  SmallGrid,
  usePageContext,
} from '@xingternal/pages-common';
import * as Types from '@xingternal/pages-graphql-types';

import DeleteDialog from './DeleteDialog';
import { useEntityPageUpdateCoverMutation } from './UpdateCover.gql-types';
import { useEntityPageDeleteCoverMutation } from './DeleteCover.gql-types';

import { UpdateCrop } from '../Common';

import styles from '../edit.css';

import UpsellBannerContainer, {
  UpsellBannerCompanyHeaderBanner,
  UpsellBannerUploadImage,
} from '../../UpsellBanner/UpsellBannerContainer';

export const ENTITY_PAGES_UNFENCE_HEADER: string =
  'entity_pages_unfence_header';

type RenderEditCoverMenuProps = {
  newOriginalCover?: string | null;
  hasCover?: boolean;
  isFreePage?: boolean;
  refetchCover?: () => void;
};

const EditCoverMenu = (props: RenderEditCoverMenuProps) => (
  <DialogContextProvider>
    <RenderEditCoverMenu {...props} />
  </DialogContextProvider>
);

const RenderEditCoverMenu: React.FC<RenderEditCoverMenuProps> = ({
  newOriginalCover,
  hasCover,
  isFreePage,
  refetchCover,
}) => {
  const popOver = usePopOver();
  const [isEditCrop, setIsEditCrop] = useState<boolean | null | undefined>(
    null
  );
  const [showDialog, setShowDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const { featureSwitches } = useContext(XingFrameContext);
  const { showError } = useContext(ErrorContext);
  const { pageContext, setPageContext } = usePageContext();
  const pageSlug = pageContext.pageSlug;

  const hasFenceFS = featureSwitches?.includes(ENTITY_PAGES_UNFENCE_HEADER);
  const isFreeHeaderProfile = () =>
    hasFenceFS &&
    isFreePage &&
    pageContext.focusType === Types.EntityPageFocusType.Company;

  const closeOmView = useRef<() => void | null>();

  const {
    setDataChanged,
    dataChanged,
    dialogConfirmation,
    setDialogConfirmation,
  } = useDialogContext();

  const setNewCover = (
    newCover?: string | null,
    newOriginalCover?: string | null
  ) => {
    setPageContext({
      ...pageContext,
      coverMedia: {
        ...pageContext.coverMedia,
        newCover,
        newOriginalCover,
      },
    });
  };

  const displayError = (error: ErrorType) => {
    showError({
      message: 'EP_GENERAL_FEEDBACK_ERROR',
      error,
    });
  };

  const [updateCover] = useEntityPageUpdateCoverMutation({
    onCompleted: (data) =>
      data?.entityPageUpdateCover?.error &&
      displayError(data.entityPageUpdateCover.error),
    onError: (error) => displayError(error),
  });

  const [deleteCover] = useEntityPageDeleteCoverMutation({
    onCompleted: (data) => {
      if (data?.entityPageDeleteCover?.error) {
        displayError(data.entityPageDeleteCover.error);
      }
    },
    onError: (error) => displayError(error),
  });

  const handleClick = (activateOmView: () => void, isEditCrop?: boolean) => {
    if (isFreeHeaderProfile()) {
      trackEditOpening({
        isFreePage,
        focusType: pageContext.focusType,
        itemId: pageContext.pageId,
        module: 'header',
        part: 'image',
      });
    } else {
      trackEditOpening({
        focusType: pageContext.focusType,
        itemId: pageContext.pageId,
        module: 'header',
        part: 'image',
      });
    }
    setIsEditCrop(isEditCrop);
    popOver.handleHide();
    activateOmView();
  };

  const handleDeleteClick = () => {
    popOver.handleHide();
    setShowDialog(true);
  };

  const discard = () => {
    if (dataChanged) {
      setDialogConfirmation({
        dialogAction: () => {
          setDialogConfirmation(null);
          setDataChanged(false);
          closeOmView?.current && closeOmView.current();
        },
      });
    } else {
      closeOmView?.current && closeOmView.current();
    }
  };

  const onUpload = async (
    coverUploadId: any,
    originalCoverUploadId: any,
    handleClose: () => void,
    cover: string,
    originalCover: string
  ) => {
    const { data } = await updateCover({
      variables: {
        id: pageSlug,
        coverMediaType: Types.EntityPageCoverMediaType.Image,
        coverMediaUploadId: coverUploadId,
        originalCoverMediaUploadId: originalCoverUploadId,
      },
    });

    if (!data?.entityPageUpdateCover?.error) {
      setNewCover(cover, originalCover);
      setDataChanged(false);
      handleClose();
    }
  };

  const addMobileGuideline = () => {
    const mobileGuideline = document.getElementById('mobileGuideline');
    if (!mobileGuideline) {
      const node = document.createElement('div');
      node.id = 'mobileGuideline';
      node.className = `${styles.mobileGuidelineContainer}`;
      const x = document.getElementsByClassName('reactEasyCrop_CropArea')[0];

      x.appendChild(node);
    }
  };

  return (
    <>
      <DeleteDialog
        showDialog={showDialog}
        loading={isLoading}
        onCancel={() => setShowDialog(false)}
        onConfirm={async () => {
          setIsLoading(true);
          const { data } = await deleteCover({ variables: { id: pageSlug } });

          if (!data?.entityPageDeleteCover?.error) {
            if (isFreeHeaderProfile())
              trackDelete({
                focusType: pageContext.focusType,
                itemId: pageContext.pageId,
                isFreePage,
              });
            setNewCover(null, null);
            refetchCover && (await refetchCover());
          }
          setIsLoading(false);
          setShowDialog(false);
        }}
      />
      <EditButton
        size={'small'}
        showIcon
        className={styles.coverEditButton}
        data-cy="EDIT_BUTTON_ON_COVER_IMAGE"
        data-testid={'EDIT_BUTTON_ON_COVER_IMAGE'}
        onClick={popOver.handleShow}
        innerRef={popOver.triggerRef}
      />
      <OmView
        interceptClose={() => discard()}
        trigger={(activateOmView: () => void) => (
          <Menu
            dimmerTitle="close menu"
            onOutsideClick={popOver.handleHide}
            triggerRef={popOver.triggerRef}
            show={popOver.show}
          >
            <span>
              <SelectList className={styles.listWrapper}>
                <SelectListOption
                  className={styles.listItem}
                  data-cy="COVER_IMAGE_UPLOAD"
                  data-testid={'COVER_IMAGE_UPLOAD'}
                  onClick={() => handleClick(activateOmView)}
                >
                  <IconUpload width={24} height={24} />
                  <BodyCopy noMargin size={'small'} className={styles.link}>
                    <FormattedMessage
                      id="EP_UPLOAD_COVER"
                      defaultMessage="EP_UPLOAD_COVER"
                    />
                  </BodyCopy>
                </SelectListOption>
                {hasCover && (
                  <>
                    <SelectListOption
                      className={styles.listItem}
                      data-cy="COVER_IMAGE_EDIT"
                      onClick={() => handleClick(activateOmView, true)}
                    >
                      <IconImage width={24} height={24} />
                      <BodyCopy noMargin size={'small'} className={styles.link}>
                        <FormattedMessage
                          id="EP_EDIT_SECTION"
                          defaultMessage="EP_EDIT_SECTION"
                        />
                      </BodyCopy>
                    </SelectListOption>
                    <SelectListOption
                      className={styles.listItem}
                      data-cy="COVER_IMAGE_DELETE"
                      onClick={handleDeleteClick}
                    >
                      <IconTrash width={24} height={24} />
                      <BodyCopy noMargin size={'small'} className={styles.link}>
                        <FormattedMessage
                          id="EP_DELETE_COVER"
                          defaultMessage="EP_DELETE_COVER"
                        />
                      </BodyCopy>
                    </SelectListOption>
                  </>
                )}
              </SelectList>
            </span>
          </Menu>
        )}
      >
        {({ handleClose }: { handleClose: () => void }) => {
          closeOmView.current = handleClose;

          return (
            <SmallGrid>
              {isFreeHeaderProfile() && (
                <UpsellBannerContainer initialStep={0}>
                  <UpsellBannerUploadImage
                    isEditCrop={!!isEditCrop}
                    setNewCover={setNewCover}
                    addMobileGuideline={addMobileGuideline}
                    discard={discard}
                    newOriginalCover={newOriginalCover}
                    handleClose={handleClose}
                  />
                  <UpsellBannerCompanyHeaderBanner
                    onUpload={onUpload}
                    handleClose={handleClose}
                  />
                </UpsellBannerContainer>
              )}
              {!isFreePage && (
                <>
                  {isEditCrop ? (
                    <UpdateCrop
                      isCover={true}
                      descriptionCopyKey={'EP_COVER_CROP_DESCRIPTION'}
                      setDataChanged={setDataChanged}
                      setNewCover={setNewCover}
                      onDragCrop={() => setDataChanged(true)}
                      onCropReady={() => addMobileGuideline()}
                      onDiscard={() => discard()}
                      showError={showError}
                      newOriginal={newOriginalCover}
                      pageSlug={pageSlug}
                      closeOmview={handleClose}
                      imageType={'cover'}
                      aspectRatio={8 / 2}
                      onPublish={() => {
                        trackEditSaving({
                          focusType: pageContext.focusType,
                          itemId: pageContext.pageId,
                          module: 'header',
                          part: 'image',
                        });
                      }}
                    />
                  ) : (
                    <ImageUpload
                      descriptionCopyKey={'EP_COVER_CROP_DESCRIPTION'}
                      onCropReady={() => addMobileGuideline()}
                      onDiscard={
                        dataChanged ? () => discard() : () => handleClose()
                      }
                      onChange={() => setDataChanged(true)}
                      showError={showError}
                      shouldUploadOriginal={true}
                      onPublish={(
                        coverUploadId,
                        cover,
                        originalCoverUploadId,
                        originalCover
                      ) => {
                        trackEditSaving({
                          focusType: pageContext.focusType,
                          itemId: pageContext.pageId,
                          module: 'header',
                          part: 'image',
                        });
                        onUpload(
                          coverUploadId,
                          originalCoverUploadId,
                          handleClose,
                          cover,
                          originalCover
                        );
                      }}
                      aspectRatio={8 / 2}
                      imageUploadCopy={{
                        headline: 'EP_CHOOSE_COVER',
                        hint: 'EP_UPLOAD_IMAGE_HINT',
                        label: 'EP_UPLOAD_COVER_LABEL',
                        onDragLabel: 'EP_UPLOAD_IMAGE_DRAG_LABEL',
                      }}
                    />
                  )}
                  <ExternalEditInfoBanner
                    copyKey="EP_CHOOSE_COVER_INFO_BANNER"
                    ctaUrl={`https://help.onlyfy.com/hc/de/articles/8803872010001-Erstbef%C3%BCllung-des-Employer-Branding-Profils-auf-XING#titel--/-headerbild`}
                    ctaCopyKey="EP_CHOOSE_COVER_INFO_BANNER_CTA"
                  />
                </>
              )}
            </SmallGrid>
          );
        }}
      </OmView>
      {dataChanged && dialogConfirmation?.dialogAction && <NotSavedDialog />}
    </>
  );
};

export default EditCoverMenu;
